import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/ui/Avatar'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { placeName } from '@/lib/countries'
import { splitMoney } from '@/lib/format'
import { MODE_ICONS, modeLabelKey } from '@/lib/icons'
import type { CaseSummary, LegStatus } from '@/types/domain'

import { LegPlace } from '@/features/cases/components/legs'
import { profitSign } from '@/features/cases/lib/profit'

/* The board form of a case — the twin of QuoteCard, and built for the same
 * reason that file states: a list answers "which record is this", a board
 * answers "can this one be moved on today". The generic RecordCard the cases
 * board used before carried three fields, because it also has to serve the
 * phone list, where three fields is the whole budget.
 *
 * Reading order is fixed and top-down, because a lane is scanned vertically and
 * every card must put the same thing in the same place:
 *
 *   number                 identity — the handle people say out loud
 *   client                 who it is for
 *   route · mode           where the shipment goes, and how
 *   ── figures ──          rate, profit, ETD, ETA, on a rule of their own
 *   owner + stalled        who holds it, and whether it has stopped moving
 *
 * The figures sit under a hairline in a right-aligned column, exactly as the
 * quote card's do: everything above the rule is prose about ONE case, everything
 * below it is a number that means something read against the card above and
 * below. The money leads and the dates follow, because "what is this worth" is
 * asked of the lane and "when does it land" is asked of the card.
 *
 * Status is absent by design — the lane already says it (RecordBoard's
 * renderCard contract). On the phone, where lanes become headed groups, the
 * heading still carries it, and the phone LIST keeps its own RecordCard.
 */

export interface CaseCardProps {
  case: CaseSummary
}

/** The mode disc restates the leg's state as a tint — the same five tones
 *  `LegCard` uses, kept identical on purpose: two maps that agree today is how
 *  a lane and a record come to disagree about what "waiting" looks like. */
const LEG_DISC: Record<LegStatus, string> = {
  planned: 'bg-neutral-100 text-text-muted',
  'in-transit': 'bg-brand-soft text-brand',
  'waiting-border': 'bg-attention-soft text-attention',
  arrived: 'bg-positive-soft text-positive',
  completed: 'bg-positive-soft text-positive',
}

export function CaseCard({ case: row }: CaseCardProps) {
  const { t, i18n } = useTranslation()
  const format = useFormat()
  /* The LEG's mode, not the case's: a case moves by road and then by rail, and
     the disc is about the movement under way. The disc is drawn only when there
     IS a current leg, and a leg always has a mode, so nothing here falls back
     to the case's own — which may be absent (a NULL column). */
  const LegModeIcon = row.currentLeg ? MODE_ICONS[row.currentLeg.mode] : undefined

  /* The route line: where it goes and how. Each part is dropped rather than
     defaulted when absent — an em dash between two blanks says less than no
     line at all, and a case created against a client before its lane is known
     has neither end of the route yet. */
  const origin = placeName(row.origin, i18n.language)
  const destination = placeName(row.destination, i18n.language)
  const lane = origin && destination ? `${origin} → ${destination}` : origin || destination

  const detail = [lane, row.mode ? t(modeLabelKey(row.mode)) : undefined]
    .filter(Boolean)
    .join(' · ')

  const rate = row.rate ? splitMoney(row.rate.amount, row.rate.currency) : null
  /* The row's ONE profit (S-6): the server's `profit` — the forecast while
     the case runs, the cash once it is financially closed. */
  const profit = row.profit ? splitMoney(row.profit.amount, row.profit.currency) : null
  /* A loss is marked by weight and sign as well as colour — the same rule the
     profit COLUMN follows, and for the same reason: roughly one man in twelve
     would otherwise read a negative figure as a positive one. */
  const losing = profitSign(row.profit?.amount) === 'loss'

  return (
    <div className="flex flex-col gap-2.5">
      {/* The number never wraps and never truncates: it is the handle people say
          out loud, and half of it is not a handle. */}
      <div className="flex items-baseline gap-2">
        <span className="text-brand shrink-0 text-[13px] font-semibold whitespace-nowrap">
          {row.number}
        </span>
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-text-heading truncate text-[13.5px]">
          {row.clientName}
        </span>
        {detail ? (
          <span className="text-text-muted truncate text-[12px] font-light">
            {detail}
          </span>
        ) : null}
      </div>

      {/* WHERE THE CARGO IS, drawn the way the record's own leg card draws a leg
          — the mode disc tinted by the leg's state, the position, the state in
          words, and each end of the movement wearing its country's flag. One
          vocabulary for one thing: somebody who has read the route section
          recognises this at a glance, and the flags and the disc are the part
          that survives being scanned at speed down a lane.

          A miniature rather than `LegCard` itself: that card is a card's worth
          of facts — carrier, plates, CMR, dates, cost — and a lane 256px wide
          holds none of it. What it shares is the parts that carry meaning:
          `LEG_DISC`'s tint, `MODE_ICONS`'s icon, and `LegPlace`, which is
          imported rather than re-expressed so the ABSENT country keeps
          rendering as it did before the field existed.

          Colour never carries the state alone: the disc is
          tinted AND the state is spelled beside the position. */}
      {row.currentLeg ? (
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-full',
              LEG_DISC[row.currentLeg.status],
            )}
          >
            {LegModeIcon ? <LegModeIcon size={13} /> : null}
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-text-body truncate text-[12px] leading-tight">
              {t('cases.card.leg', {
                sequence: row.currentLeg.sequence,
                total: row.currentLeg.total,
              })}
              <span className="text-text-faint"> · </span>
              {t(`legStatus.${row.currentLeg.status}`)}
            </span>
            {row.currentLeg.origin || row.currentLeg.destination ? (
              <span className="text-text-muted truncate text-[11.5px] leading-tight font-light">
                <LegPlace
                  name={row.currentLeg.origin ?? ''}
                  country={row.currentLeg.originCountry}
                />
                <span className="text-text-faint mx-1" aria-hidden>
                  →
                </span>
                <LegPlace
                  name={row.currentLeg.destination ?? ''}
                  country={row.currentLeg.destinationCountry}
                />
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      {/* The figures. A hairline above rather than a filled block: the card is
          already a surface, and a second surface inside it would read as a
          nested card. Labels left, values right — the label column is read once
          and then ignored, the value column is what the eye runs down. The whole
          block disappears when a case has none of the four, which a case created
          from a bare header genuinely can. */}
      {rate || profit || row.etd || row.eta ? (
        <dl className="border-line-hair flex flex-col gap-1 border-t pt-2.5">
          {rate ? (
            <Figure label={t('cases.card.rate')}>
              <span className="asa-num text-text-heading text-[13px] font-medium">
                {rate.amount}
              </span>
              <span className="text-text-faint ml-1 text-[11px]">{rate.currency}</span>
            </Figure>
          ) : null}

          {profit ? (
            <Figure label={t('cases.card.profit')}>
              <span
                className={
                  losing
                    ? 'asa-num text-negative text-[12.5px] font-semibold'
                    : 'asa-num text-text-body text-[12.5px]'
                }
              >
                {profit.amount}
              </span>
              <span className="text-text-faint ml-1 text-[11px]">
                {profit.currency}
              </span>
            </Figure>
          ) : null}

          {row.etd ? (
            <Figure label={t('cases.card.etd')}>
              <span className="asa-num text-text-body text-[12.5px]">
                {format.date(row.etd)}
              </span>
            </Figure>
          ) : null}

          {row.eta ? (
            <Figure label={t('cases.card.eta')}>
              <span className="asa-num text-text-body text-[12.5px]">
                {format.date(row.eta)}
              </span>
            </Figure>
          ) : null}
        </dl>
      ) : null}

      {/* Owner and flag share the last line: "who has this" and "what is wrong
          with it" are the two things asked at standup, and they belong to each
          other. The line is dropped entirely when a case has neither.

          The stalled marker stays on the board even though the lane may already
          say `stuck`: the two are different facts. `stuck` is a status somebody
          set; `daysStalled` is the row's HEALTH, and an in-progress case nobody
          has touched for a week is exactly the card this board exists to
          surface. */}
      {row.ownerName || row.daysStalled ? (
        <div className="flex items-center gap-2 pt-0.5">
          {row.ownerName ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <Avatar name={row.ownerName} size="sm" />
              <span className="text-text-muted truncate text-[12px] font-light">
                {row.ownerName}
              </span>
            </span>
          ) : null}
          <span className="flex-1" />
          {row.daysStalled ? (
            <span
              title={t('cases.stalled', { count: row.daysStalled })}
              className="bg-attention-soft text-attention flex shrink-0 items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[11px] font-medium"
            >
              <TriangleAlert size={12} aria-hidden />
              {t('cases.card.stalledShort', { count: row.daysStalled })}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** One label/value row of the figures block. */
function Figure({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-muted text-[11.5px] font-light">{label}</dt>
      <dd className="shrink-0 text-right">{children}</dd>
    </div>
  )
}
